// src/pages/ForgotPassword.jsx
import { useState } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';
import { logger } from '@/lib/logger';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const { resetPasswordForEmail } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSending(true);

    try {
      logger.debug('Sending password reset request');
      const result = await resetPasswordForEmail(email);
      logger.debug('Password reset API response received');

      // Force a small delay to ensure the HTTP request is fully flushed
      // This works around browser connection buffering issues
      await new Promise(resolve => setTimeout(resolve, 100));

      logger.debug('Password reset email should be sent now');
      setSent(true);
    } catch (err) {
      logger.error('Password reset error:', err);
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-2xl text-center">
              Sprawdz swoja skrzynke
            </CardTitle>
            <p className="text-center text-slate-500 text-sm mt-2">
              Wyslalismy link do resetowania hasla na adres:
            </p>
            <p className="text-center font-medium text-slate-700 mt-1">
              {email}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
              <p className="font-medium mb-1">Co dalej?</p>
              <ol className="list-decimal list-inside space-y-1 text-blue-600">
                <li>Otworz email od KConnect</li>
                <li>Kliknij w link do resetowania hasla</li>
                <li>Ustaw nowe haslo</li>
              </ol>
            </div>

            <p className="text-xs text-slate-400 text-center">
              Nie widzisz maila? Sprawdź folder spam.
            </p>
            <p className="text-xs text-slate-400 text-center">
              Resetowałeś przed chwilą hasło? Odczekaj 5 minut.
            </p>

            <Link to="/login" className="block">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Powrot do logowania
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <CardTitle className="text-2xl text-center">
            Zapomniałeś hasła?
          </CardTitle>
          <p className="text-center text-slate-500 text-sm mt-2">
            Podaj adres email powiazany z Twoim kontem, a wyslemy Ci link do resetowania hasla.
          </p>
        </CardHeader>
        <CardContent>
          {error && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              error.includes('Zbyt wiele')
                ? 'bg-amber-50 border border-amber-200 text-amber-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {error.includes('Zbyt wiele') && (
                <span className="mr-2">⏱️</span>
              )}
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Adres email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="twoj@email.com"
                className="mt-1"
              />
            </div>

            <Button
              type="submit"
              disabled={sending || !email}
              className="w-full"
            >
              {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Wyslij link do resetowania
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link
              to="/login"
              className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" />
              Powrot do logowania
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
